import { API_URL } from '../services/api';
import { idleSeconds } from './userActivity';

// Streaming client for POST /api/ai/chat (axios cannot stream). The server
// answers with Server-Sent Events: `data: <json>\n\n` per event, `: ping`
// heartbeats in between (docs/ai-module-design-2026-10-30.md §7.1, §8.1).
//
//   non-2xx  -> throws an Error carrying { status, code, error } (the JSON body
//               the server sends); 401 also redirects to the login page, the
//               same way the axios interceptor does.
//   200      -> onEvent(json) per event until the server ends the response.
//   aborting `signal` rejects with an AbortError — the caller decides what that means.

const failure = (status, code, message) => {
  const err = new Error(message);
  err.status = status;
  err.code = code;
  err.error = message;
  return err;
};

// One SSE block (text between blank lines) -> parsed event or null.
const parseBlock = (block) => {
  const data = block
    .split('\n')
    .filter((line) => line.startsWith('data:'))
    .map((line) => line.slice(5).replace(/^ /, ''))
    .join('\n');
  if (!data) return null; // comment/heartbeat block (": ping") or empty
  try {
    return JSON.parse(data);
  } catch (e) {
    return null;
  }
};

export async function streamAiChat({ conversationId, message, pagePath, onEvent, signal }) {
  const res = await fetch(`${API_URL}/ai/chat`, {
    method: 'POST',
    credentials: 'include',
    signal,
    headers: {
      'Content-Type': 'application/json',
      'X-User-Idle-Seconds': String(idleSeconds()),
    },
    body: JSON.stringify({ conversation_id: conversationId || undefined, message, page_path: pagePath }),
  });

  if (!res.ok) {
    let body = null;
    try { body = await res.json(); } catch (e) { /* not JSON */ }
    if (res.status === 401 && window.location.pathname !== '/login') {
      const idle = String(res.headers.get('x-session-ended') || '') === 'idle';
      window.location.href = idle ? '/login?reason=idle' : '/login';
    }
    throw failure(res.status, body && body.code, (body && (body.error || body.message)) || `Request failed (${res.status})`);
  }
  if (!res.body || !res.body.getReader) throw failure(res.status, 'AI_INTERNAL', 'This browser cannot stream the answer.');

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  const drain = (final) => {
    buffer = buffer.replace(/\r\n/g, '\n');
    let at;
    while ((at = buffer.indexOf('\n\n')) >= 0) {
      const ev = parseBlock(buffer.slice(0, at));
      buffer = buffer.slice(at + 2);
      if (ev) onEvent(ev);
    }
    if (final && buffer.trim()) {
      const ev = parseBlock(buffer);
      buffer = '';
      if (ev) onEvent(ev);
    }
  };
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    drain(false);
  }
  buffer += decoder.decode();
  drain(true);
}
